import React, { createContext, useState, useContext, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { login as loginService, logout as logoutService } from '../services/authService';
import api from '../services/api';
import {
  isBiometricAvailable,
  getStoredCredential,
  storeCredential,
  createCredential,
} from '../services/webAuthnService';

const AuthContext = createContext(null);

const getInitialUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Failed to parse user from localStorage", error);
    return null;
  }
};

const getInitialTimeout = () => {
  const u = getInitialUser();
  return u?.company?.session_timeout_minutes ?? 60;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getInitialUser());
  const [companyId, setCompanyId] = useState(localStorage.getItem('companyId'));
  const [sessionTimeout, setSessionTimeout] = useState(getInitialTimeout);

  const inactivityTimerRef = useRef(null);
  const warningTimerRef    = useRef(null);
  const warningActiveRef   = useRef(false);

  const login = async (company, email, password) => {
    const data = await loginService(company, email, password);

    // Múltiplas empresas: retornar lista para o LoginPage mostrar o modal
    if (data.companies) return data;

    setUser(data.user);
    setCompanyId(data.companyId);
    setSessionTimeout(data.user?.company?.session_timeout_minutes ?? 5);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('companyId', data.companyId);

    // Não await setupBiometric aqui — o prompt aparece após a navegação (ver useEffect abaixo)
    if (await isBiometricAvailable() && !getStoredCredential() && !localStorage.getItem('biometric_offer_declined')) {
      sessionStorage.setItem('pendingBiometricOffer', '1');
    }

    return data;
  };

  const setupBiometric = async (loggedUser, loggedCompanyId) => {
    const result = await Swal.fire({
      title: 'Ativar login biométrico?',
      text: 'Entre com biometria (Touch ID, Face ID) sem precisar digitar senha.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, ativar',
      cancelButtonText: 'Agora não',
      confirmButtonColor: '#3730a3',
      cancelButtonColor: '#6c757d',
    });

    if (!result.isConfirmed) {
      localStorage.setItem('biometric_offer_declined', '1');
      return;
    }

    try {
      const optionsRes = await api.post('/webauthn/register/options');
      const { options, challenge_token } = optionsRes.data;

      const credential = await createCredential(options);

      await api.post('/webauthn/register', {
        challenge_token,
        credential,
        name: navigator.userAgent.includes('Mobile') ? 'Celular' : 'Computador',
      });

      storeCredential(loggedUser.email, loggedCompanyId);

      await Swal.fire({
        icon: 'success',
        title: 'Biometria ativada!',
        text: 'Na próxima vez, você poderá entrar sem senha.',
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        // Usuário cancelou o prompt biométrico do SO — silencioso
        return;
      }
      console.error('Biometric setup failed', err.name, err.message, err);
      await Swal.fire({
        icon: 'error',
        title: 'Não foi possível ativar a biometria',
        text: err.response?.data?.message || 'Verifique se seu dispositivo suporta autenticação biométrica e tente novamente.',
        confirmButtonText: 'Ok',
      });
    }
  };

  const logout = useCallback(async () => {
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(warningTimerRef.current);
    warningActiveRef.current = false;
    try {
      await logoutService();
    } catch (_) {
      // ignora erro de rede — limpa estado local de qualquer forma
    }
    setUser(null);
    setCompanyId(null);
    localStorage.removeItem('user');
    localStorage.removeItem('companyId');
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (warningActiveRef.current) return;

    clearTimeout(inactivityTimerRef.current);
    clearTimeout(warningTimerRef.current);

    const totalMs = sessionTimeout * 60 * 1000;
    const warnMs  = Math.min(60, Math.floor(sessionTimeout * 60 * 0.2)) * 1000;

    warningTimerRef.current = setTimeout(async () => {
      warningActiveRef.current = true;

      const result = await Swal.fire({
        title: 'Sessão prestes a expirar',
        html: 'Sua sessão será encerrada por inatividade em <strong></strong> segundos.',
        icon: 'warning',
        timer: warnMs,
        timerProgressBar: true,
        showCancelButton: false,
        confirmButtonText: 'Manter logado',
        confirmButtonColor: '#3730a3',
        didOpen: () => {
          const b = Swal.getHtmlContainer().querySelector('strong');
          let remaining = Math.round(warnMs / 1000);
          b.textContent = remaining;
          const interval = setInterval(() => {
            remaining -= 1;
            if (b) b.textContent = remaining;
          }, 1000);
          Swal.getPopup()._countdownInterval = interval;
        },
        willClose: () => {
          clearInterval(Swal.getPopup()._countdownInterval);
        },
      });

      warningActiveRef.current = false;

      if (result.isConfirmed) {
        resetInactivityTimer();
      } else if (result.dismiss === Swal.DismissReason.timer) {
        logout();
      }
    }, totalMs - warnMs);

    inactivityTimerRef.current = setTimeout(() => {
      logout();
    }, totalMs);
  }, [sessionTimeout, logout]);

  // Exibe o prompt biométrico após a navegação pós-login (evita bloquear window.location.href)
  useEffect(() => {
    if (!user) return;
    if (!sessionStorage.getItem('pendingBiometricOffer')) return;
    sessionStorage.removeItem('pendingBiometricOffer');
    setupBiometric(user, companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach(e => document.removeEventListener(e, resetInactivityTimer));
      clearTimeout(inactivityTimerRef.current);
      clearTimeout(warningTimerRef.current);
    };
  }, [user, resetInactivityTimer]);

  const updateSessionTimeout = useCallback((minutes) => {
    setSessionTimeout(minutes);
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    if (stored?.company) {
      stored.company.session_timeout_minutes = minutes;
      localStorage.setItem('user', JSON.stringify(stored));
    }
  }, []);

  const value = {
    user,
    companyId,
    isAuthenticated: !!user,
    sessionTimeout,
    login,
    logout,
    updateSessionTimeout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
